/**
 * Icon Error Handler
 *
 * NOTE: Icon validation primarily happens at BUILD TIME via utils/validateAssetIcons.mjs
 * This runtime handler is a fallback for edge cases and development.
 *
 * Build-time validation ensures:
 * - All coin icons exist for assets in supported-assets-data.json
 * - All protocol icons exist with correct abbreviations
 * - Build fails if any required icons are missing
 *
 * This runtime handler catches:
 * - Network failures loading valid icons
 * - Development when new assets are added
 * - Edge cases not caught at build time
 */
export const handleIconError = (event, iconType, identifier) => {
  const iconPath = event.currentTarget.src;
  const errorMessage = `Missing ${iconType} icon for ${identifier}: ${iconPath}`;

  // Always log the error
  console.error(errorMessage);
  console.error(`Icon type: ${iconType}`);
  console.error(`Identifier: ${identifier}`);
  console.error(`Path attempted: ${iconPath}`);

  // In development, throw an error to make it immediately visible
  if (process.env.NODE_ENV === 'development') {
    // Prevent infinite error loops
    event.currentTarget.onerror = null;
    throw new Error(errorMessage);
  } else {
    // In production, set a placeholder to avoid broken UI
    // But still log the error for monitoring
    event.currentTarget.onerror = null;
    // Use a clear placeholder that indicates missing icon
    event.currentTarget.src =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzJBMkEyQSIvPgo8dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NjY2NiIgZm9udC1zaXplPSIyNCI+PzwvdGV4dD4KPC9zdmc+';
    event.currentTarget.alt = `Missing ${iconType}`;
  }
};

/**
 * @deprecated Use build-time validation instead (utils/validateAssetIcons.mjs)
 *
 * This runtime validation is kept for backward compatibility but is no longer needed
 * as icon validation now happens at build time, preventing deployment with missing icons.
 */
export const validateAllIcons = async (data) => {
  const missingIcons = [];

  for (const item of data) {
    // Check coin icon
    const coinIconPath = `/images/lp/coins/icons/${item.ticker.toLowerCase()}.png`;
    try {
      const response = await fetch(coinIconPath, { method: 'HEAD' });
      if (!response.ok) {
        missingIcons.push({ type: 'coin', ticker: item.ticker, path: coinIconPath });
      }
    } catch (error) {
      missingIcons.push({ type: 'coin', ticker: item.ticker, path: coinIconPath });
    }

    // Check protocol icons
    for (const protocol of item.protocols) {
      const protocolIconPath = `/images/lp/supported-assets/protocols/icons/${protocol.toLowerCase()}.svg`;
      try {
        const response = await fetch(protocolIconPath, { method: 'HEAD' });
        if (!response.ok) {
          missingIcons.push({ type: 'protocol', protocol, path: protocolIconPath });
        }
      } catch (error) {
        missingIcons.push({ type: 'protocol', protocol, path: protocolIconPath });
      }
    }
  }

  if (missingIcons.length > 0) {
    console.error('Missing icons detected:', missingIcons);
    if (process.env.NODE_ENV === 'development') {
      console.error('Fix these missing icons before deployment!');
    }
  }

  return missingIcons;
};
